/**
 * The operator console: every account on the platform, the companies it can
 * reach, and the role it holds in each.
 *
 * Not company-scoped — it sits outside the /c/:slug shell, because the point is
 * the view across tenants. Every write here is a super-user action and lands in
 * the affected company's audit log.
 *
 * The row is the user rather than the membership. "Who is this person and where
 * can they get to" is the question this page answers, and a flat membership
 * list makes the reader assemble that themselves.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, api, unwrap } from "../api";
import {
  Button,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Panel,
  Table,
  TableSearch,
  Td,
  Th,
  input,
} from "../ui";

const ROLES = [
  ["viewer", "Viewer"],
  ["accountant", "Accountant"],
  ["manager", "Manager"],
  ["admin", "Admin"],
  ["owner", "Owner"],
] as const;

function roleLabel(role: string) {
  return ROLES.find(([v]) => v === role)?.[1] ?? role;
}

type AdminUser = Awaited<ReturnType<typeof fetchUsers>>["users"][number];

async function fetchUsers() {
  return unwrap(await api.api.admin.users.$get());
}

export function Admin({
  onOpenCompany,
  onCreateCompany,
}: {
  onOpenCompany: (slug: string) => void;
  onCreateCompany: () => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  /** Narrows the Accounts list to members of one company. Empty is everyone. */
  const [companyFilter, setCompanyFilter] = useState("");
  /** Which user's "add to company" row is open. One at a time is enough. */
  const [adding, setAdding] = useState<string | null>(null);

  const users = useQuery({ queryKey: ["admin-users"], queryFn: fetchUsers });
  const companies = useQuery({
    queryKey: ["admin-companies"],
    queryFn: async () => unwrap(await api.api.admin.companies.$get()),
  });

  function refresh() {
    qc.invalidateQueries({ queryKey: ["admin-users"] });
    qc.invalidateQueries({ queryKey: ["admin-companies"] });
    // The switcher and every company-scoped list are downstream of a
    // membership change, including this operator's own reach.
    qc.invalidateQueries({ queryKey: ["me"] });
  }

  const run = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onMutate: () => setError(null),
    onSuccess: () => {
      setAdding(null);
      refresh();
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : "Something went wrong. Try again."),
  });

  if (users.isPending || companies.isPending) return <Loading />;
  if (users.isError) return <ErrorNote>Couldn't load accounts.</ErrorNote>;

  const allCompanies = companies.data?.companies ?? [];

  /**
   * Owners per company, so the last one can be shown as such rather than
   * letting the operator discover the rule by hitting a 409.
   */
  const owners = new Map<string, number>();
  for (const u of users.data.users) {
    for (const m of u.memberships) {
      if (m.role === "owner") owners.set(m.companyId, (owners.get(m.companyId) ?? 0) + 1);
    }
  }

  const q = query.trim().toLowerCase();
  const rows = users.data.users
    .filter((u) => !companyFilter || u.memberships.some((m) => m.companyId === companyFilter))
    .filter((u) =>
      q
        ? `${u.email} ${u.name} ${u.memberships.map((m) => `${m.companyName} ${m.role}`).join(" ")}`
            .toLowerCase()
            .includes(q)
        : true,
    );

  return (
    <>
      <div className="mb-6">
        <h1 className="font-[family-name:--font-display] text-xl font-semibold tracking-tight text-neutral-900">
          Platform administration
        </h1>
        <p className="mt-1 max-w-prose text-sm text-neutral-500">
          Every account on the platform and the companies it can reach. Changes here are super-user
          actions and are written to the affected company's audit log.
        </p>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      <Panel
        title={`Accounts (${rows.length})`}
        action={
          <div className="flex items-center gap-2">
            <select
              className={`${input} w-48`}
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
            >
              <option value="">All companies</option>
              {allCompanies.map((co) => (
                <option key={co.id} value={co.id}>
                  {co.name}
                </option>
              ))}
            </select>
            <TableSearch value={query} onChange={setQuery} placeholder="Search accounts…" />
          </div>
        }
      >
        {rows.length === 0 ? (
          <Empty title="No accounts match that filter." />
        ) : (
          <Table
            head={
              <>
                <Th>Account</Th>
                <Th>Companies and roles</Th>
                <Th />
              </>
            }
          >
            {rows.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                companies={allCompanies}
                owners={owners}
                busy={run.isPending}
                adding={adding === u.id}
                onToggleAdd={() => setAdding(adding === u.id ? null : u.id)}
                onOpenCompany={onOpenCompany}
                run={(fn) => run.mutate(fn)}
              />
            ))}
          </Table>
        )}
      </Panel>

      <Panel
        title={`Companies (${allCompanies.length})`}
        action={<Button onClick={onCreateCompany}>New company</Button>}
      >
        <Table
          head={
            <>
              <Th>Company</Th>
              <Th>Address</Th>
              <Th>Members</Th>
              <Th />
            </>
          }
        >
          {allCompanies.map((co) => (
            <CompanyRow key={co.id} company={co} onOpenCompany={onOpenCompany} />
          ))}
        </Table>
      </Panel>
    </>
  );
}

function CompanyRow({
  company,
  onOpenCompany,
}: {
  company: { id: string; slug: string; name: string; archivedAt: string | null; members: number };
  onOpenCompany: (slug: string) => void;
}) {
  const [addingClient, setAddingClient] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.api.c[":slug"].clients.$post({
          param: { slug: company.slug },
          json: { name: name.trim() },
        }),
      ),
    onMutate: () => setError(null),
    onSuccess: () => {
      setAddingClient(false);
      setName("");
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Couldn't add that client."),
  });

  return (
    <>
      <tr className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100">
        <Td>
          {company.name}
          {company.archivedAt && <span className="ml-2 text-xs text-neutral-400">archived</span>}
        </Td>
        <Td mono muted>
          /c/{company.slug}
        </Td>
        <Td muted>{company.members}</Td>
        <Td right>
          <div className="flex justify-end gap-1">
            {!company.archivedAt && (
              <Button
                variant="ghost"
                onClick={() => {
                  setError(null);
                  setAddingClient((v) => !v);
                }}
              >
                {addingClient ? "Cancel" : "Add client"}
              </Button>
            )}
            <Button variant="ghost" onClick={() => onOpenCompany(company.slug)}>
              Open
            </Button>
          </div>
        </Td>
      </tr>
      {addingClient && (
        <tr className="border-b border-neutral-100">
          <td colSpan={4} className="bg-neutral-50/70 px-4 py-3">
            {error && <ErrorNote>{error}</ErrorNote>}
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim()) create.mutate();
              }}
            >
              <Field label={`New client for ${company.name}`}>
                <input
                  className={`${input} w-64`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Client name"
                  required
                />
              </Field>
              <Button type="submit" disabled={create.isPending || !name.trim()}>
                {create.isPending ? "Adding…" : "Add client"}
              </Button>
            </form>
          </td>
        </tr>
      )}
    </>
  );
}

function UserRow({
  user,
  companies,
  owners,
  busy,
  adding,
  onToggleAdd,
  onOpenCompany,
  run,
}: {
  user: AdminUser;
  companies: { id: string; slug: string; name: string; archivedAt: string | null }[];
  owners: Map<string, number>;
  busy: boolean;
  adding: boolean;
  onToggleAdd: () => void;
  onOpenCompany: (slug: string) => void;
  run: (fn: () => Promise<unknown>) => void;
}) {
  const [addCompany, setAddCompany] = useState("");
  const [addRole, setAddRole] = useState<string>("viewer");

  // Companies they aren't in yet — the only ones an "add" could target.
  const available = companies.filter(
    (co) => !co.archivedAt && !user.memberships.some((m) => m.companyId === co.id),
  );

  return (
    <tr className="border-b border-neutral-100 align-top even:bg-neutral-50 transition-colors hover:bg-neutral-100">
      <Td>
        <div>{user.email}</div>
        {user.name && <div className="text-xs text-neutral-500">{user.name}</div>}
        {user.isSuperUser && (
          <div className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
            Super user — reaches every company
          </div>
        )}
      </Td>

      <Td>
        {user.memberships.length === 0 ? (
          <span className="text-sm text-neutral-400">
            {user.isSuperUser ? "No memberships — reaches companies as an operator" : "No company"}
          </span>
        ) : (
          <div className="flex flex-col gap-2">
            {user.memberships.map((m) => {
              // Demoting or removing the last owner locks a company out of its
              // own settings, so the control is not offered.
              const lastOwner = m.role === "owner" && (owners.get(m.companyId) ?? 0) === 1;
              const elsewhere = companies.filter(
                (co) =>
                  !co.archivedAt &&
                  co.id !== m.companyId &&
                  !user.memberships.some((x) => x.companyId === co.id),
              );
              return (
                <div key={m.id} className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onOpenCompany(m.companySlug)}
                    className="text-sm font-medium text-accent-700 hover:underline"
                  >
                    {m.companyName}
                  </button>

                  {lastOwner ? (
                    <span className="text-sm text-neutral-600">
                      {roleLabel(m.role)}
                      <span className="ml-1.5 text-xs text-neutral-400">last owner</span>
                    </span>
                  ) : (
                    <select
                      className={`${input} w-32`}
                      value={m.role}
                      disabled={busy}
                      onChange={(e) =>
                        run(() =>
                          unwrap(
                            api.api.admin.memberships[":id"].$patch({
                              param: { id: m.id },
                              json: { role: e.target.value as never },
                            }),
                          ),
                        )
                      }
                    >
                      {ROLES.map(([v, label]) => (
                        <option key={v} value={v}>
                          {label}
                        </option>
                      ))}
                    </select>
                  )}

                  {/* One step, not remove-then-add: the two-step version leaves
                      them in neither company if the second call fails, and
                      trips the last-owner rule on the way out. */}
                  {elsewhere.length > 0 && (
                    <select
                      className={`${input} w-40`}
                      value=""
                      disabled={busy}
                      onChange={(e) => {
                        const to = e.target.value;
                        if (!to) return;
                        run(() =>
                          unwrap(
                            api.api.admin.memberships[":id"].move.$post({
                              param: { id: m.id },
                              json: { companyId: to },
                            }),
                          ),
                        );
                      }}
                    >
                      <option value="">Move to…</option>
                      {elsewhere.map((co) => (
                        <option key={co.id} value={co.id}>
                          {co.name}
                        </option>
                      ))}
                    </select>
                  )}

                  {!lastOwner && (
                    <Button
                      variant="ghost"
                      disabled={busy}
                      onClick={() =>
                        run(() =>
                          unwrap(api.api.admin.memberships[":id"].$delete({ param: { id: m.id } })),
                        )
                      }
                    >
                      Remove
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {adding && available.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-neutral-100 pt-3">
            <select
              className={`${input} w-48`}
              value={addCompany}
              onChange={(e) => setAddCompany(e.target.value)}
            >
              <option value="">Choose a company…</option>
              {available.map((co) => (
                <option key={co.id} value={co.id}>
                  {co.name}
                </option>
              ))}
            </select>
            <select
              className={`${input} w-32`}
              value={addRole}
              onChange={(e) => setAddRole(e.target.value)}
            >
              {ROLES.map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </select>
            <Button
              disabled={busy || !addCompany}
              onClick={() =>
                run(() =>
                  unwrap(
                    api.api.admin.memberships.$post({
                      json: { userId: user.id, companyId: addCompany, role: addRole as never },
                    }),
                  ),
                )
              }
            >
              Add
            </Button>
          </div>
        )}
      </Td>

      <Td right>
        {available.length > 0 && (
          <Button variant="ghost" onClick={onToggleAdd}>
            {adding ? "Cancel" : "Add to company"}
          </Button>
        )}
      </Td>
    </tr>
  );
}
